/**
 * The *rich reading blocks*: a `content` block plus a presentation.
 *
 * They are not separate slide types on purpose. A quote, a note, a captioned
 * figure, a table and a formula all carry the same things a content block does — a
 * title, prose, an optional reading voice — and differ only in how the theme
 * prints them. Keeping them on `content` means one editor, one audio field and one
 * stored shape, and a new theme gets all of them by declaring its `presentation`
 * styles.
 *
 * The metadata lives here rather than in one studio because *both* studios offer
 * them: a capsule may carry a quote or a formula exactly as a book may, and the
 * picker's rows are data (see `#/lib/studioBlockPicker`). One list, so a new rich
 * kind appears in both pickers and neither screen has a second copy to drift.
 */
import {type MessageDescriptor} from '@lingui/core'
import {msg} from '@lingui/core/macro'

import {CONTENT_KIND_ACCENT} from '#/lib/slideTokens'
import {type StudioPickerEntry} from '#/lib/studioBlockPicker'
import {type Slide} from '#/types/capsule'

/** How each rich kind names itself in the picker and in a block's own header. */
export const RICH_KIND_META: Record<
  NonNullable<Slide['contentKind']>,
  {
    label: MessageDescriptor
    desc: MessageDescriptor
    icon: string
  }
> = {
  text: {
    label: msg`Content`,
    desc: msg`Text or explanatory content`,
    icon: 'layout',
  },
  quote: {
    label: msg`Quote`,
    desc: msg`Set a passage apart`,
    icon: 'message-square',
  },
  callout: {
    label: msg`Note`,
    desc: msg`A tip, a warning or an aside`,
    icon: 'info',
  },
  caption: {
    label: msg`Image & caption`,
    desc: msg`A picture with a credit`,
    icon: 'image',
  },
  table: {
    label: msg`Table`,
    desc: msg`Rows and columns of data`,
    icon: 'grid',
  },
  formula: {
    label: msg`Formula`,
    desc: msg`A mathematical expression`,
    icon: 'code',
  },
}

/**
 * Whether a reading block of this kind has anything worth hearing lives next door
 * (`#/lib/spokenKinds`), because the fixtures need it and could not import it from
 * here — this module's labels need the translation macros, and the browser suite
 * imports the fixtures in plain Node. Re-exported so a UI that already names a
 * kind's label does not have to reach for a second import to ask about its voice.
 */
export {kindSpeaks, offersReadAloud, SPOKEN_KINDS} from '#/lib/spokenKinds'

/**
 * The rich rows, ready for the shared picker.
 *
 * They ride along beside the plain text block in the section its type belongs to,
 * each arriving already knowing its kind (`patch`) so a rich block is never briefly
 * plain on the way in.
 */
export const RICH_PICKER_ENTRIES: StudioPickerEntry[] = (
  Object.keys(RICH_KIND_META) as NonNullable<Slide['contentKind']>[]
)
  .filter(kind => kind !== 'text')
  .map(kind => {
    const meta = RICH_KIND_META[kind]
    return {
      key: `content:${kind}`,
      type: 'content' as const,
      patch: {contentKind: kind},
      label: meta.label,
      desc: meta.desc,
      icon: meta.icon,
      color: CONTENT_KIND_ACCENT[kind],
    }
  })
